import type { ItemLite } from "@plannello/shared";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useCatalog } from "@/data/CatalogProvider";
import type { Catalog } from "@/data/catalog";
import { useProductItems } from "@/data/items";
import { api } from "@/lib/api";

/**
 * The work queue ("Next up") computed in the browser from the cached rows, with exactly the
 * server's rules (apps/server/src/queue.ts), so reordering and live edits show at once:
 *
 *   candidates  open items that are started or unstarted, or P0, or carry `review-finding`
 *   hidden      candidates blocked by an open item
 *   order       P0, then review findings, then manual rank (ties by id)
 *
 * Blockers outside the loaded products are unknown here; for those the server's queue
 * (GET /products/:key/queue, also used for `blockedHidden`) decides.
 */

export type QueueTier = "p0" | "review-finding" | "ranked";
export const QUEUE_TIERS: QueueTier[] = ["p0", "review-finding", "ranked"];
export const QUEUE_LABEL = "review-finding";

export type QueueEntry = { item: ItemLite; tier: QueueTier };

type ServerQueue = { items: Array<{ item: ItemLite; tier: QueueTier }>; total: number; blockedHidden: number };

export const serverQueueQuery = (productKey: string) =>
  queryOptions({
    queryKey: ["queue", productKey.toUpperCase()],
    queryFn: ({ signal }) => api.get<ServerQueue>(`/api/v1/products/${productKey}/queue`, signal),
    staleTime: 30_000,
  });

const isOpenCategory = (c: string | undefined) => c !== "completed" && c !== "canceled";

export function buildQueue(
  items: readonly ItemLite[],
  catalog: Catalog,
  opts: { serverIds?: ReadonlySet<string> } = {},
): { entries: QueueEntry[]; blocked: ItemLite[] } {
  const findingIds = new Set(catalog.labels.filter((l) => l.slug === QUEUE_LABEL).map((l) => l.id));
  const byId = new Map(items.map((i) => [i.id, i]));
  const category = (i: ItemLite) => catalog.statusById.get(i.statusId)?.category;
  const isFinding = (i: ItemLite) => i.labelIds.some((l) => findingIds.has(l));
  const isBlocked = (i: ItemLite) =>
    i.blockedByIds.some((id) => {
      const b = byId.get(id);
      // Unknown blocker (another product): trust the server's membership.
      if (!b) return opts.serverIds ? !opts.serverIds.has(i.id) : false;
      return isOpenCategory(category(b));
    });
  const entries: QueueEntry[] = [];
  const blocked: ItemLite[] = [];
  for (const i of items) {
    if (i.archivedAt) continue;
    const c = category(i);
    if (!isOpenCategory(c)) continue;
    const candidate = c === "started" || c === "unstarted" || i.priority === 1 || isFinding(i);
    if (!candidate) continue;
    if (isBlocked(i)) {
      blocked.push(i);
      continue;
    }
    entries.push({ item: i, tier: i.priority === 1 ? "p0" : isFinding(i) ? "review-finding" : "ranked" });
  }
  const tierIndex = (t: QueueTier) => QUEUE_TIERS.indexOf(t);
  entries.sort(
    (a, b) =>
      tierIndex(a.tier) - tierIndex(b.tier) ||
      (a.item.rank < b.item.rank ? -1 : a.item.rank > b.item.rank ? 1 : a.item.id < b.item.id ? -1 : 1),
  );
  return { entries, blocked };
}

/** The queue of one product, live from the cache. */
export function useQueue(productId: string | undefined, productKey: string | undefined) {
  const catalog = useCatalog();
  const all = useProductItems(productId ? [productId] : []);
  const server = useQuery({ ...serverQueueQuery(productKey ?? ""), enabled: !!productKey });
  const serverIds = useMemo(
    () => (server.data ? new Set(server.data.items.map((e) => e.item.id)) : undefined),
    [server.data],
  );
  const queue = useMemo(
    () => (catalog.ready ? buildQueue(all.items, catalog, { serverIds }) : { entries: [], blocked: [] }),
    [all.items, catalog, serverIds],
  );
  return { ...queue, all, isPending: all.isPending || !catalog.ready, isError: all.isError, refetch: all.refetch };
}

/** Backlog items (not yet candidates) in rank order: what can be pulled into Next up. */
export function backlogOf(items: readonly ItemLite[], catalog: Catalog): ItemLite[] {
  const findingIds = new Set(catalog.labels.filter((l) => l.slug === QUEUE_LABEL).map((l) => l.id));
  return items
    .filter((i) => {
      if (i.archivedAt) return false;
      const c = catalog.statusById.get(i.statusId)?.category;
      const s = catalog.statusById.get(i.statusId);
      return c === "backlog" && s?.slug !== "triage" && i.priority !== 1 && !i.labelIds.some((l) => findingIds.has(l));
    })
    .sort((a, b) => (a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : a.id < b.id ? -1 : 1));
}
